/**
 * Gelbes Vorschau-Banner (git-checkout-Geste, Variant D).
 *
 * Erscheint im Feld, wenn eine frühere Version vorgeschaut wird: zeigt Metadaten
 * der Version + [Abbrechen] / [Diese Version wiederherstellen].
 */

import { useTranslation } from 'react-i18next'
import { Eye, RotateCcw } from 'lucide-react'
import { badgeFor } from './activityFeed'
import type { ContentBlockVersion } from '@/types/versioning'

interface Props {
  version: ContentBlockVersion
  onCancel: () => void
  onRestore: () => void
}

export function FieldPreviewBanner({ version, onCancel, onRestore }: Props) {
  const { t, i18n } = useTranslation()
  const badge = badgeFor(version)

  return (
    <div className="content-preview-banner flex items-center justify-between gap-3 mb-2 px-3 py-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-900/20">
      <div className="flex items-center gap-2 min-w-0">
        <span className="flex h-7 w-7 items-center justify-center rounded bg-amber-300 text-amber-900 shrink-0">
          <Eye className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {t('productContent.channels.versions.previewTitle')}
            <span className="font-mono font-normal text-xs ml-1">· {new Date(version.created_at).toLocaleString(i18n.language)}</span>
          </div>
          <div className="text-xs text-amber-800/80 dark:text-amber-300/80 truncate">
            {version.changed_by_name ?? 'System'} · {t(`productContent.channels.activity.badge.${badge}`)}
            {version.diff_summary ? ` · ${version.diff_summary}` : ''}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" className="text-xs px-2.5 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="button" className="flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-primary-500 text-white" onClick={onRestore}>
          <RotateCcw className="h-3 w-3" />{t('productContent.channels.versions.restoreThis')}
        </button>
      </div>
    </div>
  )
}
